import { getVideoThumbnailUrl } from "@/api/renderer/api";
import { VideoInsight } from "@/api/studio/insights/types/insight.type";
import { Benchmark } from "@/api/studio/library/types/benchmark.type";
import { getBenchmarkComparison } from "@/api/studio/library/utils/get-benchmark-comparison";
import { getBenchmarkZoneWidths } from "@/api/studio/library/utils/get-benchmark-zone-widths";
import { cn } from "@/utils/classes";

/* Prop Types */
interface VideoBenchmarkChartProps {
  insights: VideoInsight[];
  benchmark: Benchmark;
  projectId: string;
  studyId: string;
}

const PERCENT_TICKS = [0, 20, 40, 60, 80, 100];

const BORDER_COLOR_BY_STATUS = {
  below: "border-[#f02e2e]",
  within: "border-[#d06200]",
  above: "border-[#1d8510]",
};

/**
 * VideoBenchmarkChart component
 *
 * Renders the benchmark overview: a graph splitting the 0-100% attention score axis into
 * below/within/above-benchmark zones with each video positioned along it by score, and an
 * "Applied benchmark" panel showing the benchmark's name, sample size, and range.
 */
export const VideoBenchmarkChart = ({ insights, benchmark, projectId, studyId }: VideoBenchmarkChartProps) => {
  /* Consts */
  const { belowPercent, withinPercent, abovePercent } = getBenchmarkZoneWidths(benchmark);

  /* Render */
  return (
    <div className="grid grid-cols-[1fr_420px] gap-4 items-start px-4">
      <p className="col-span-2 font-semibold">Benchmark</p>

      <div className="flex flex-col gap-2">
        {/* Graph */}
        <div className="relative h-28 w-full">
          <div className="absolute inset-y-0 border-r border-dashed border-[#9ca3af] bg-[rgba(240,46,46,0.1)]" style={{ left: "0%", width: `${belowPercent}%` }} />
          <div className="absolute inset-y-0 border-r border-dashed border-[#9ca3af] bg-[rgba(208,98,0,0.1)]" style={{ left: `${belowPercent}%`, width: `${withinPercent}%` }} />
          <div className="absolute inset-y-0 bg-[rgba(29,133,16,0.1)]" style={{ left: `${belowPercent + withinPercent}%`, width: `${abovePercent}%` }} />

          {insights.map((insight) => {
            const score = Math.min(100, Math.max(0, insight.creative.attention.score));
            const { status } = getBenchmarkComparison(insight.creative.attention.score, benchmark);

            return (
              <div
                key={insight.assetId}
                className={cn("absolute top-4 flex flex-col items-center gap-0.5 rounded-lg border-2 bg-base-100 p-1 w-[62px]", BORDER_COLOR_BY_STATUS[status])}
                style={{ left: `${score}%`, transform: "translateX(-50%)" }}
              >
                <img src={getVideoThumbnailUrl({ projectId, studyId, assetKey: insight.key })} alt={insight.assetName} className="h-9 w-full rounded object-cover" />
                <p className="w-full truncate text-center text-xs text-base-content/60">{insight.assetName}</p>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex gap-0.5 w-full">
          <div
            className="flex items-center justify-center rounded overflow-hidden truncate whitespace-nowrap text-[10px] font-medium text-base-content bg-[rgba(240,46,46,0.6)]"
            style={{ width: `${belowPercent}%` }}
          >
            Below benchmark
          </div>
          <div
            className="flex items-center justify-center rounded overflow-hidden truncate whitespace-nowrap text-[10px] font-medium text-base-content bg-[rgba(208,98,0,0.6)]"
            style={{ width: `${withinPercent}%` }}
          >
            Within benchmark
          </div>
          <div
            className="flex items-center justify-center rounded overflow-hidden truncate whitespace-nowrap text-[10px] font-medium text-base-content bg-[rgba(29,133,16,0.5)]"
            style={{ width: `${abovePercent}%` }}
          >
            Above benchmark
          </div>
        </div>

        {/* Percentage ticks */}
        <div className="flex justify-between w-full text-[8px] text-base-content/60">
          {PERCENT_TICKS.map((tick) => (
            <span key={tick}>{tick}%</span>
          ))}
        </div>
      </div>

      {/* Applied benchmark panel */}
      <div className="flex flex-col gap-3">
        <p className="text-sm font-semibold text-base-content/60">Applied benchmark</p>
        <div className="flex items-center justify-between rounded-lg bg-base-200 p-4">
          <div className="flex flex-col gap-1">
            <p className="font-semibold">{benchmark.name}</p>
            <p className="text-sm text-base-content/70">{benchmark.videoCount.toLocaleString()} videos</p>
          </div>
          <p className="font-semibold">
            {benchmark.scoreRangeMin}-{benchmark.scoreRangeMax}%
          </p>
        </div>
      </div>
    </div>
  );
};
